import type {GraphDataPoint} from '@src/types/statistics.ts';

export const LAST_CONFIGURATION_ID_LOCAL_STORAGE_KEY =
  'pl-analyze-last-configuration-id';
export const PROMOTE_AUTOMATIC_ZOOM_TO_COLLECTION_LOCAL_STORAGE_KEY =
  'pl-promote-automatic-zoom-to-collection';

export const API_PROCESS_FILE_IDENTIFIER_TYPES = {
  default: 'default',
  superResRgb: 'rgb',
  superResConfidence: 'confidence',
} as const;
export type ApiProcessFileIdentifierType =
  (typeof API_PROCESS_FILE_IDENTIFIER_TYPES)[keyof typeof API_PROCESS_FILE_IDENTIFIER_TYPES];

export const MOSAICKING_ORDER = {
  mostRecent: 'mostRecent',
  leastRecent: 'leastRecent',
  leastCC: 'leastCC',
} as const;
export type MosaickingOrderValue =
  (typeof MOSAICKING_ORDER)[keyof typeof MOSAICKING_ORDER];

// Process API supports also "lanczos" and "box", but the resampling is expected to change.
// So, let's keep the same options as in older apps.
// see https://hello.planet.com/code/sentinel-hub/sentinel-frontend/browser/-/issues/178#note_5722381
export const INTERPOLATOR = {
  bilinear: 'BILINEAR',
  bicubic: 'BICUBIC',
  nearest: 'NEAREST',
} as const;
export type InterpolatorValue =
  (typeof INTERPOLATOR)[keyof typeof INTERPOLATOR];

export const SUPER_RES_LAYER_ID = 'super-res';

export const ASSET_TYPE_TO_SUPER_RES_EVALSCRIPT_ID: Record<string, string> = {
  ortho_analytic_4b: '2f84557a-1453-4c5b-bf77-d4892a3fdd78:2',
  ortho_analytic_8b: '2f84557a-1453-4c5b-bf77-d4892a3fdd78:2',
  ortho_analytic_4b_sr: '5a780173-89fc-4f39-9f99-a024f256c713:2',
  ortho_analytic_8b_sr: '5a780173-89fc-4f39-9f99-a024f256c713:2',
};

export const SUPER_RES_RESOLUTION = 2;
// Calculated based on optimal (waiting time and reliability) pixel count for super-res process request
export const SUPER_RES_AOI_AREA_RANGE = {
  min: 500_000,
  max: 10_000_000,
} as const;
export const SUPER_RES_AOI_OPTIMAL_AREA = 1_500_000;
export const MAX_PROCESS_API_SIZE_PER_SIDE = 2500;

export const PreviewModeString = {
  detail: 'DETAIL',
  preview: 'PREVIEW',
  extendedPreview: 'EXTENDED_PREVIEW',
} as const;
export type PreviewModeStringValue =
  (typeof PreviewModeString)[keyof typeof PreviewModeString];

export const PREVIEW_LAYER_KIND = {
  marker: 'AOI_MARKER',
  geometry: 'AOI_GEOMETRY',
} as const;
export type PreviewLayerKindValue =
  (typeof PREVIEW_LAYER_KIND)[keyof typeof PREVIEW_LAYER_KIND];

type ResolutionLimits = {
  resolution: number;
  resolutionCeiling: number;
};

export const DEFAULT_RESOLUTION_CEILING = 2000;
export const STATS_COLLECTION_RESOLUTION_CONFIG: Record<
  string,
  ResolutionLimits
> = {
  // Public datasets
  'sentinel-2-l1c': {
    resolution: 10,
    resolutionCeiling: DEFAULT_RESOLUTION_CEILING,
  },
  'sentinel-2-l2a': {resolution: 10, resolutionCeiling: 1400},
  'aws-lot-l1': {resolution: 15, resolutionCeiling: 1490},
  'aws-lot-l2': {resolution: 30, resolutionCeiling: 1490},
  // BYOC - Default conservative settings
  byoc: {resolution: 0.5, resolutionCeiling: DEFAULT_RESOLUTION_CEILING},
};
export const STATS_EVALSCRIPT_OUTPUT = 'eobrowserStats';

export const STATS_CSV_EXPORT_FIELDS: (keyof GraphDataPoint)[] = [
  'date',
  'min',
  'max',
  'mean',
  'stDev',
  'sampleCount',
  'noDataCount',
  'median',
  'p10',
  'p90',
  'cloudCoveragePercent',
];

export const COLLECTION_MIN_RESOLUTION: Record<string, MeterPerPixel> = {
  'sentinel-1-grd': 1500,
  'sentinel-2-l1c': 200 - 50, // 200 by spec - 50m buffer for reliability
  'sentinel-2-l2a': 1500,
  'landsat-ot-l1': 1500,
  'landsat-ot-l2': 1500,
  hls: 1500,
  'psscene-browse': 450,
  // Below are needed until https://hello.planet.com/code/sentinel-hub/sentinel-core/java/-/issues/6235
  // Analysis ready PlanetScope
  'byoc-3f605f75-86c4-411a-b4ae-01c896f0e54e': 500,
  // PlanetScope
  'byoc-28eef896-9632-4546-a99e-cea34d74b21e': 180,
  // SkySat
  'byoc-fc704520-fc81-439f-9016-5e162c32e736': 240,
  // PV FCD Abovegroud carbon
  'byoc-cc31cada-80d8-46fe-a746-43ac2f87b5da': 398,
  // PV FCD Canopy Height
  'byoc-f3312c82-edea-42a1-8c9d-ada86ddcc857': 398,
  // PV FCD Canopy Cover
  'byoc-e3d2a21c-cb75-4311-86ac-024385c85b9c': 398,
  // PV FCM Aboveground Carbon
  'byoc-d4a2a179-0c1a-4426-8d54-e2ac82830e83': 415,
  // PV FCM Aboveground Height
  'byoc-d09d8fd8-a3b3-49fb-9d78-47f3a5cc8ecc': 112,
  // PV FCM Aboveground Cover
  'byoc-ca501757-cf8e-43a8-b1a4-1aa59ae22425': 112,
  // PV LST 100m
  'byoc-8d977093-cf9e-4351-8159-90f2522c29c1': 500,
  // PV LST 1km
  'byoc-6b613b07-410a-4312-93ad-d1751fdc55de': 500,
  // PV SWC 100m
  'byoc-65f7e4fb-a27a-4fae-8d79-06a59d7e6ede': 500,
  // PV SWC 1km
  'byoc-858254ae-0f29-4152-ac53-449efa00bbb0': 3947,
  // Planet Monthly Basemaps
  'byoc-c48c018f-67a1-4827-a1e4-f3ab98690312': 500,
  // Planet Monthly Roads and Buildings
  'byoc-9ff30ab7-62c2-4d19-b5ef-83805c7e7602': 1528,
  default: 500,
  zarrDefault: 30,
} as const;

export type CollectionMinResolutionKey = keyof typeof COLLECTION_MIN_RESOLUTION;
